// src/components/EventAccessUI.jsx
import React from "react";
import { Box, Button, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { STYLE_PRESETS } from "./CoverStylePicker";
import { isDarkColor } from "../utils/fontsandthemes";
import WesternCornerDecor from "./WesternCornerDecor";

// -----------------------------------------------------------------------------
// CONSTANTS
// -----------------------------------------------------------------------------

const DEFAULT_COVER_URL =
  "https://firebasestorage.googleapis.com/v0/b/scan-cap.appspot.com/o/STYLE%20PAGE%2FIMG_7025.png?alt=media&token=c288528e-15b1-41fd-b9d8-46f06cc1eef2";

const CHAMPAGNE_BG =
  "radial-gradient(circle at top, #f9e7dd 0%, #fdf2f8 35%, #111827 100%)";

const WESTERN_BG =
  "linear-gradient(180deg, #2a1b10 0%, #2a1b10 18%, #d9b38c 38%, #c48a5a 72%, #2a1b10 100%)";

const FROZEN_BG =
  "radial-gradient(circle at top, #38bdf8 0%, #0f172a 55%, #020617 100%)";

const POLAROID_BG = "linear-gradient(180deg, #0f172a 0%, #1e3a8a 100%)";

const CLASSIC_BG =
  "radial-gradient(circle at top, #111827 0%, #020617 60%, #000000 100%)";

const BLACKTIE_BG =
  "radial-gradient(circle at top, #1f2937 0%, #020617 55%, #000000 100%)";

// -----------------------------------------------------------------------------
// SHARED GATE STATE HELPERS (unchanged from your version)
// -----------------------------------------------------------------------------

export const lockedStates = ["DURING_LOCKED", "STRICT_SELF_VIEW"];

export function computeBoundaries(event) {
  // Optional Luxon (if globally loaded)
  let LuxonDT = null;
  try {
    LuxonDT =
      typeof window !== "undefined" &&
      window.luxon &&
      window.luxon.DateTime
        ? window.luxon.DateTime
        : null;
  } catch {
    /* noop */
  }

  const normalizeToTimestampLike = (val) => {
    if (!val) return null;
    if (typeof val.toMillis === "function") return val; // Firestore Timestamp-like
    if (val instanceof Date) return { toMillis: () => val.getTime() };
    if (typeof val === "string" || typeof val === "number") {
      const d = new Date(val);
      if (!isNaN(d.getTime())) return { toMillis: () => d.getTime() };
    }
    return null;
  };

  let startUTC = normalizeToTimestampLike(event?.startUTC);
  let endUTC = normalizeToTimestampLike(event?.endUTC);

  // If you already have explicit endUTC/startUTC stored, use them.
  if (startUTC && endUTC) return { startUTC, endUTC };

  const rawDate =
    event?.eventDateLabel ||
    event?.details?.eventDateLabel ||
    event?.details?.eventDate ||
    event?.eventDate ||
    event?.customization?.eventDate;

  if (!rawDate) return { startUTC, endUTC };

  const tz =
    event?.hostTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Prefer strict YYYY-MM-DD parsing (your old behavior)
  const ymdMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(rawDate).trim());

  // ✅ Best path: Luxon present → compute end-of-day in hostTimezone correctly
  if (LuxonDT) {
    let startLocal;
    if (ymdMatch) {
      startLocal = LuxonDT.fromFormat(String(rawDate).trim(), "yyyy-MM-dd", {
        zone: tz,
      });
    } else {
      startLocal = LuxonDT.fromJSDate(new Date(rawDate), { zone: tz });
    }

    if (startLocal && startLocal.isValid) {
      const start = startLocal.startOf("day").toUTC();
      const end = startLocal.endOf("day").toUTC();
      return { startUTC: start, endUTC: end };
    }
  }

  // Fallback (no Luxon)
  const d = ymdMatch
    ? new Date(
        Number(ymdMatch[1]),
        Number(ymdMatch[2]) - 1,
        Number(ymdMatch[3]),
        0,
        0,
        0,
        0
      )
    : new Date(rawDate);

  if (!isNaN(d.getTime())) {
    const start = new Date(d);
    start.setHours(0, 0, 0, 0);

    const end = new Date(d);
    end.setHours(23, 59, 59, 999);

    return {
      startUTC: { toMillis: () => start.getTime() },
      endUTC: { toMillis: () => end.getTime() },
    };
  }

  return { startUTC, endUTC };
}

function deriveHasContributed({ viewer, event, userId }) {
  if (viewer?.hasContributed) return true;
  if (event?.contributions && userId && event.contributions[userId]) {
    return true;
  }
  return false;
}

export const getGalleryState = ({ event, viewer, userId, nowUtcMs, isDemo }) => {
  if (!event) return "LOADING";

  const nowMs = typeof nowUtcMs === "number" ? nowUtcMs : Date.now();
  const { endUTC } = computeBoundaries(event);

  const strict = !!(
    event?.strictSurprise ?? event?.customization?.strictSurprise
  );

  // STRICT SURPRISE
  if (strict) {
    const endMs = endUTC?.toMillis?.();
    if (!endMs || nowMs <= endMs) {
      return "STRICT_SELF_VIEW";
    }
    return "POST_UNLOCKED";
  }

  // NORMAL EVENTS
  if (isDemo) return "POST_UNLOCKED";
  if (!endUTC) return "POST_UNLOCKED";

  const endMs = endUTC.toMillis();
  const contributed = deriveHasContributed({ viewer, event, userId });

  if (nowMs <= endMs) {
    if (contributed) return "DURING_UNLOCKED";
    return "DURING_LOCKED";
  }

  return "POST_UNLOCKED";
};

// -----------------------------------------------------------------------------
// STYLE HELPERS
// -----------------------------------------------------------------------------

function getBasePreset(styleId) {
  return (
    (STYLE_PRESETS && STYLE_PRESETS.find((p) => p.id === styleId)) ||
    STYLE_PRESETS.find((p) => p.id === "scarlet") ||
    STYLE_PRESETS.find((p) => p.id === "classic") ||
    STYLE_PRESETS[0]
  );
}

function isColorCoverStyle(styleId) {
  return (
    styleId === "blacktie" ||
    styleId === "sage" ||
    styleId === "scarlet" ||
    styleId === "blush" ||
    styleId === "champagne"
  );
}


function getOuterBackground(styleId, customization, basePreset) {
  if (styleId === "western") return WESTERN_BG;
if (styleId === "champagne") {
  return `
    linear-gradient(
      180deg,
      rgba(15,23,42,0.05) 0%,
      rgba(15,23,42,0.65) 70%,
      rgba(0,0,0,0.9) 100%
    ),
    radial-gradient(
      circle at top,
      #f9e7dd 0%,
      #fdf2f8 35%,
      #111827 100%
    )
  `;
}
  if (styleId === "frozen") return FROZEN_BG;
  if (styleId === "polaroid") return POLAROID_BG;
  if (styleId === "blacktie") return BLACKTIE_BG;
  if (styleId === "classic") return CLASSIC_BG;

  // Sage / Red / Pink use their preset background
  if (styleId === "sage" || styleId === "scarlet" || styleId === "blush") {
    return basePreset?.bg || customization.backgroundColor || "#020617";
  }

  // fallback
  return customization.backgroundColor || basePreset?.bg || "#020617";
}

// ---- title contrast helpers ----
function extractLastHexColor(input) {
  if (typeof input !== "string") return null;
  const hexes = input.match(/#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})\b/g);
  if (!hexes || !hexes.length) return null;
  return hexes[hexes.length - 1];
}

function isBgDark(bg) {
  if (typeof bg === "string" && bg.trim().startsWith("#")) {
    return isDarkColor(bg.trim());
  }
  const lastHex = extractLastHexColor(bg);
  if (lastHex) return isDarkColor(lastHex);
  return true;
}

function applyReadableTitle({ styleId, titleColor, dateColor, outerBackground }) {
  // ✅ WESTERN: force white title/date (matches your mobile preview expectation)
  if (styleId === "western") {
    return {
      titleColor: "rgba(255,255,255,0.98)",
      dateColor: "rgba(255,255,255,0.82)",
      titleShadow: "0 10px 28px rgba(0,0,0,0.45)",
      dateShadow: "0 8px 20px rgba(0,0,0,0.35)",
    };
  }

  const dark = isBgDark(outerBackground);

  const safeTitle = dark ? "rgba(255,255,255,0.98)" : "rgba(17,24,39,0.95)";
  const safeDate = dark ? "rgba(255,255,255,0.82)" : "rgba(17,24,39,0.72)";

  // Keep your champagne behavior
  if (styleId === "champagne") {
    return {
      titleColor,
      dateColor,
      titleShadow:
        "0 2px 10px rgba(255,255,255,0.75), 0 10px 28px rgba(0,0,0,0.12)",
      dateShadow:
        "0 1px 8px rgba(255,255,255,0.65), 0 8px 22px rgba(0,0,0,0.10)",
    };
  }

  // Default: readable everywhere
  const titleShadow = dark
    ? "0 10px 28px rgba(0,0,0,0.45)"
    : "0 10px 26px rgba(0,0,0,0.12)";
  const dateShadow = dark
    ? "0 8px 20px rgba(0,0,0,0.35)"
    : "0 8px 18px rgba(0,0,0,0.10)";

  return {
    titleColor: safeTitle,
    dateColor: safeDate,
    titleShadow,
    dateShadow,
  };
}

// -----------------------------------------------------------------------------
// HERO IMAGE RENDERERS – match presets but stay responsive
// -----------------------------------------------------------------------------

function renderHeroImage({ styleId, photoBg, title, date }) {
  // ✅ WESTERN: match mobile preview:
  // - no extra bars/blocks (no whitespace)
  // - SVG frame sits on top
  // - photo is inset so it reads "inside" the SVG frame
  if (styleId === "western") {
    const FRAME = "#5a3418";

    return (
      <Box
        sx={{
          px: { xs: 0, sm: 0 },
          pb: { xs: 0.6, sm: 0.8 },
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: "100%",
            maxWidth: 520,
            aspectRatio: "1 / 1",
            overflow: "visible",
            pointerEvents: "none",
          }}
        >
          {/* SVG frame ON TOP (outside-photo visuals) */}
          <Box sx={{ position: "absolute", inset: 0, zIndex: 20 }}>
            <WesternCornerDecor
              color={FRAME}
              opacity={0.92}
              bleed={18}
              scale={1.08}
              zIndex={20}
            />
          </Box>

          {/* Photo INSIDE the frame (inset so frame is visible around it) */}
          <Box
            sx={{
              position: "absolute",
              inset: 38, // controls how "inside" the photo sits
              zIndex: 10,
              overflow: "hidden",
              borderRadius: 8,
              backgroundColor: "#f5e6d3",
              boxShadow: "0 12px 26px rgba(0,0,0,0.35)",
            }}
          >
            <Box
              component="img"
              src={photoBg}
              alt="Event cover"
              sx={{
                width: "100%",
                height: "100%",
                display: "block",
                objectFit: "cover",
                objectPosition: "50% 24%",
              }}
            />
          </Box>
        </Box>
      </Box>
    );
  }

  // POLAROID: complete guest-facing Polaroid card
  if (styleId === "polaroid") {
    const preset = getBasePreset("polaroid");

    return (
      <Box
        sx={{
          width: "100%",
          pt: { xs: 3.5, sm: 5 },
          pb: { xs: 1.5, sm: 2 },
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            width: "78%",
            maxWidth: 390,
            aspectRatio: "3 / 4",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            p: { xs: 1.25, sm: 1.5 },
            background: "linear-gradient(180deg, #ffffff 0%, #f4f3ef 100%)",
            border: "1px solid rgba(255,255,255,0.8)",
            boxShadow:
              "0 24px 55px rgba(0,0,0,0.48), 0 3px 10px rgba(0,0,0,0.22)",
            transform: "rotate(-3deg)",
            transformOrigin: "center center",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              height: "73%",
              flexShrink: 0,
              overflow: "hidden",
              backgroundColor: "#e5e7eb",
            }}
          >
            <Box
              component="img"
              src={photoBg}
              alt="Event cover"
              sx={{
                width: "100%",
                height: "100%",
                display: "block",
                objectFit: "cover",
                objectPosition: "50% 50%",
              }}
            />
          </Box>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              px: 1,
              pt: { xs: 1.4, sm: 1.7 },
              pb: 0.5,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "flex-start",
              textAlign: "center",
            }}
          >
            <Typography
              sx={{
                maxWidth: "100%",
                color: preset.titleColor,
                fontFamily: preset.titleFont,
                fontSize: { xs: 20, sm: 23 },
                fontWeight: 650,
                lineHeight: 1.15,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {title}
            </Typography>

            {!!date && (
              <Typography
                sx={{
                  mt: 0.6,
                  color: preset.dateColor,
                  fontFamily: preset.dateFont,
                  fontSize: { xs: 13, sm: 14 },
                  fontWeight: 500,
                  lineHeight: 1.2,
                  letterSpacing: 0.25,
                }}
              >
                {date}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    );
  }

  // BLUSH / SAGE / ELEGANT NAVY / SCARLET: shared premium cover layout
  if (isColorCoverStyle(styleId)) {
    const preset = getBasePreset(styleId);

    return (
      <Box
        sx={{
          width: "100%",
          pt: { xs: 9, sm: 6 },
          pb: { xs: 0.75, sm: 1 },
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: "100%",
            maxWidth: 560,
            aspectRatio: "5 / 6",
            overflow: "hidden",
            borderRadius: { xs: 2.5, sm: 3 },
            backgroundColor: "#020617",
            border: "1px solid rgba(255,255,255,0.16)",
            boxShadow:
              "0 22px 55px rgba(0,0,0,0.42), 0 0 34px rgba(255,255,255,0.05)",
          }}
        >
          <Box
            component="img"
            src={photoBg}
            alt="Event cover"
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              display: "block",
              objectFit: "cover",
              objectPosition: styleId === "blush" ? "50% 18%" : "50% 20%",
            }}
          />

          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(180deg, rgba(0,0,0,0) 42%, rgba(0,0,0,0.18) 62%, rgba(0,0,0,0.88) 100%)",
              pointerEvents: "none",
            }}
          />

          <Box
            sx={{
              position: "absolute",
              left: { xs: 2.5, sm: 3.5 },
              right: { xs: 2.5, sm: 3.5 },
              bottom: { xs: 2.5, sm: 3.5 },
              color: "#ffffff",
              textAlign: "left",
            }}
          >
            <Typography
              sx={{
                fontFamily: preset.titleFont,
                fontSize: { xs: 34, sm: 44 },
                fontWeight: 500,
                lineHeight: 1.02,
                color: "#ffffff",
                textShadow: "0 3px 16px rgba(0,0,0,0.78)",
              }}
            >
              {title}
            </Typography>

            {!!date && (
              <Typography
                sx={{
                  mt: 0.8,
                  fontFamily: preset.dateFont,
                  fontSize: { xs: 13, sm: 15 },
                  fontWeight: 500,
                  lineHeight: 1.2,
                  letterSpacing: { xs: 2.2, sm: 3 },
                  color: "rgba(255,255,255,0.9)",
                  textShadow: "0 2px 10px rgba(0,0,0,0.75)",
                }}
              >
                — {date} —
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    );
  }

  const isClassic = styleId === "classic";
  const isChampagne = styleId === "champagne";
  const isFrozen = styleId === "frozen";

  const isElegantVariant =
    styleId === "blacktie" ||
    styleId === "sage" ||
    styleId === "scarlet" ||
    styleId === "blush";

  const imageAspect =
    isClassic || isChampagne || isFrozen || isElegantVariant ? "4 / 3" : "16 / 9";

  // ✅ Hero sizing family: make Classic/Champagne/Frozen behave like Sage/Red/Pink
  const isHeroSizeFamily =
    styleId === "blacktie" ||
    styleId === "sage" ||
    styleId === "scarlet" ||
    styleId === "blush" ||
    styleId === "classic" ||
    styleId === "champagne" ||
    styleId === "frozen";

  const imageObjectPosition = isHeroSizeFamily
    ? styleId === "blush"
      ? "50% 18%"
      : "50% 24%"
    : "50% 50%";

  return (
    <Box
      sx={{
        px: isHeroSizeFamily ? { xs: 1, sm: 2 } : { xs: 2, sm: 3 },
        pb: isHeroSizeFamily ? { xs: 0.3, sm: 0.5 } : { xs: 0.6, sm: 0.8 },
      }}
    >
      <Box
        sx={{
          width: "100%",
          borderRadius: 0,
          overflow: "hidden",
          backgroundColor: "transparent",
          border: "none",
          boxShadow: "none",
        }}
      >
        <Box
          component="img"
          src={photoBg}
          alt="Event cover"
          sx={{
            width: "100%",
            display: "block",
            objectFit: "cover",
            aspectRatio: imageAspect,
            objectPosition: imageObjectPosition,
            borderRadius: 0,
          }}
        />
      </Box>
    </Box>
  );
}

// -----------------------------------------------------------------------------
// HERO CARD
// -----------------------------------------------------------------------------

function GateHeroCard({ styleId, customization, coverPhotoUrl, title, date }) {
  const basePreset = getBasePreset(styleId);
 const photoBg = coverPhotoUrl || "";


  const titleFont = basePreset.titleFont;
  const dateFont = basePreset.dateFont;

  const isChampagne = styleId === "champagne";
  const isPink = styleId === "blush";

  const outerBackground = getOuterBackground(styleId, customization, basePreset);
  React.useEffect(() => {
  const previousHtmlBackground = document.documentElement.style.background;
  const previousBodyBackground = document.body.style.background;

  document.documentElement.style.background = outerBackground;
  document.body.style.background = outerBackground;

  return () => {
    document.documentElement.style.background = previousHtmlBackground;
    document.body.style.background = previousBodyBackground;
  };
}, [outerBackground]);
  const bgIsDark = isBgDark(outerBackground);

  const initialTitleColor = isChampagne ? "#111827" : basePreset.titleColor;
  const initialDateColor = isChampagne
    ? "rgba(17,24,39,0.78)"
    : basePreset.dateColor;

  const readable = applyReadableTitle({
    styleId,
    titleColor: initialTitleColor,
    dateColor: initialDateColor,
    outerBackground,
  });

  // ✅ Hero sizing family: make Classic/Champagne/Frozen match Sage/Red/Pink sizing
  const isHeroSizeFamily =
    styleId === "blacktie" ||
    styleId === "sage" ||
    styleId === "scarlet" ||
    styleId === "blush" ||
    styleId === "classic" ||
    styleId === "champagne" ||
    styleId === "frozen";

  // ✅ Use the same family for tighter title spacing/typography
  const isFixFamily = isHeroSizeFamily;
  const isColorCoverFamily = isColorCoverStyle(styleId);

  const elegantTitleShadow = bgIsDark
    ? "0 2px 12px rgba(0,0,0,0.55)"
    : "0 2px 12px rgba(0,0,0,0.22)";

  const elegantDateShadow = bgIsDark
    ? "0 2px 10px rgba(0,0,0,0.45)"
    : "0 2px 10px rgba(0,0,0,0.18)";

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 640,
        mx: "auto",
        borderRadius: 0,
        overflow: "visible",
        boxShadow: "none",
        background: "transparent",
      }}
    >
      {styleId !== "polaroid" && (!isColorCoverFamily || !photoBg) && (
        <Box
          sx={{
            px: { xs: 2, sm: 3 },
            pt: isFixFamily ? { xs: 0.35, sm: 0.55 } : { xs: 1.0, sm: 1.2 },
            pb: isFixFamily ? { xs: 0.45, sm: 0.65 } : { xs: 0.8, sm: 1.0 },
            textAlign: "center",
          }}
        >
          <Typography
            sx={{
              fontFamily: titleFont,
              fontSize: isChampagne
                ? { xs: 24, sm: 30 }
                : isFixFamily
                ? { xs: 28, sm: 36 }
                : isPink
                ? { xs: 28, sm: 34 }
                : { xs: 20, sm: 24 },
              fontWeight: isChampagne ? 800 : 600,
              letterSpacing: styleId === "western" ? 1.4 : 0.6,
              color: readable.titleColor,
              textShadow: isFixFamily ? elegantTitleShadow : readable.titleShadow,
              lineHeight: 1.05,
            }}
          >
            {title}
          </Typography>

          {date && (
            <Typography
              sx={{
                mt: isFixFamily ? 0.15 : 0.25,
                fontFamily: dateFont,
                fontSize: isChampagne ? 14 : isFixFamily ? 14 : 13,
                fontWeight: isChampagne ? 700 : 400,
                color: readable.dateColor,
                textShadow: isFixFamily ? elegantDateShadow : readable.dateShadow,
                lineHeight: 1.2,
              }}
            >
              {date}
            </Typography>
          )}
        </Box>
      )}

      <Box
        sx={{
          transform:
            isHeroSizeFamily && !isColorCoverFamily ? "scale(1.10)" : "none",
          transformOrigin: "top center",
        }}
      >
      {photoBg ? (
  renderHeroImage({ styleId, photoBg, title, date })
) : (
  <Box
    sx={{
      px: { xs: 2, sm: 3 },
      pb: { xs: 0.6, sm: 0.8 },
    }}
  >
    <Box
      sx={{
        width: "100%",
        aspectRatio: styleId === "western" ? "1 / 1" : "4 / 3",
        borderRadius: styleId === "western" ? 2 : 0,
        border: styleId === "western" ? "2px solid rgba(255,255,255,0.28)" : "none",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        background:
          styleId === "champagne"
            ? "linear-gradient(180deg, rgba(255,255,255,0.92), rgba(249,231,221,0.55))"
            : styleId === "frozen"
            ? "linear-gradient(180deg, rgba(56,189,248,0.22), rgba(2,6,23,0.92))"
            : styleId === "western"
            ? "linear-gradient(180deg, rgba(42,27,16,0.92), rgba(196,138,90,0.55))"
            : "linear-gradient(180deg, rgba(255,255,255,0.08), rgba(0,0,0,0.55))",
      }}
    >
      {/* Optional western frame so it still feels intentional */}
      {styleId === "western" && (
        <Box sx={{ position: "absolute", inset: 0, zIndex: 2, pointerEvents: "none" }}>
          <WesternCornerDecor
            color={"#5a3418"}
            opacity={0.9}
            bleed={18}
            scale={1.08}
            zIndex={2}
          />
        </Box>
      )}

      <Box
        sx={{
          textAlign: "center",
          px: 2,
          zIndex: 3,
        }}
      >
        <Typography
          sx={{
            fontWeight: 900,
            letterSpacing: 0.6,
            fontSize: { xs: 18, sm: 22 },
            color: styleId === "champagne" ? "#111827" : "rgba(255,255,255,0.95)",
            textShadow:
              styleId === "champagne"
                ? "0 2px 10px rgba(255,255,255,0.55)"
                : "0 10px 24px rgba(0,0,0,0.55)",
          }}
        >
          Elevate
        </Typography>

      </Box>
    </Box>
  </Box>
)}

      </Box>
    </Box>
  );
}

// -----------------------------------------------------------------------------
// MAIN COMPONENT
// -----------------------------------------------------------------------------

const EventAccessUI = ({
  event,
  onUploadClick,
  onTakePhotoClick,
  onViewGalleryClick,
  coverSrcOverride, // optional: parent can pass a safe cover
  coverStyleId,
}) => {

    React.useLayoutEffect(() => {
    const resetScroll = () => {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'auto',
      });

      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };

    resetScroll();

    const frameId = window.requestAnimationFrame(resetScroll);
    const timeoutId = window.setTimeout(resetScroll, 100);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.clearTimeout(timeoutId);
    };
  }, []);

  const customization = event?.customization || {};

const styleId =
  coverStyleId ||
  customization.coverStyleId ||
  customization.styleId ||
  event?.coverStyleId ||
  event?.customization?.coverStyleId ||
  "scarlet";

const basePreset = getBasePreset(styleId);
const isColorCoverFamily = isColorCoverStyle(styleId);

  // ✅ Lower CTA buttons for the same family that has the larger hero sizing
const isFixFamily =
  styleId === "blacktie" ||
  styleId === "sage" ||
  styleId === "scarlet" ||
  styleId === "blush" ||
  styleId === "classic" ||
  styleId === "champagne" ||
  styleId === "frozen";


 // ✅ If parent provides a safe cover, use it.
// Otherwise use the event customization cover.
// If neither exists, leave it blank so we can render a branded fallback per style.
const coverPhotoUrl =
  (typeof coverSrcOverride === "string" && coverSrcOverride) ||
  customization.coverPhotoUrl ||
  customization.coverPhotoDataUrl ||
  "";


  const eventTitle =
    customization.eventTitle ||
    event?.details?.eventName ||
    event?.eventName ||
    event?.name ||
    "Your Event";

  const eventDate =
    event?.eventDateLabel ||
    event?.details?.eventDateLabel ||
    customization.eventDateLabel ||
    event?.details?.eventDate ||
    event?.eventDate ||
    customization.eventDate ||
    "";

  const surpriseMode = !!(event?.strictSurprise ?? customization.strictSurprise);

  const buttonColor = basePreset.buttonColor || "#111827";
  const buttonFontColor =
    basePreset.buttonFontColor ||
    (isDarkColor(buttonColor) ? "#ffffff" : "#111827");

  const secondaryBg =
    basePreset.secondaryButtonBgColor !== undefined
      ? basePreset.secondaryButtonBgColor
      : "#ffffff";

  const secondaryTextColor =
    basePreset.secondaryButtonFontColor !== undefined
      ? basePreset.secondaryButtonFontColor
      : buttonColor;

  const outerBackground = getOuterBackground(styleId, customization, basePreset);

  return (
    <Box
      sx={{
        position: "relative",
        left: "50%",
        right: "50%",
        marginLeft: "-50vw",
        marginRight: "-50vw",
        width: "100vw",
        minHeight: "100svh",
        marginTop: 0,
paddingTop: 0,
        background: outerBackground,
        "&::before": {
          content: '""',
          position: "fixed",
          inset: 0,
          background: outerBackground,
          zIndex: -1,
        },

        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        pt: { xs: 0.5, sm: 2.5 },
        pb: { xs: 4, sm: 6 },
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 720,
          mx: "auto",
          px: { xs: 2, sm: 3 },
        }}
      >
        <GateHeroCard
          styleId={styleId}
          customization={customization}
          coverPhotoUrl={coverPhotoUrl}
          title={eventTitle}
          date={eventDate}
        />

        <Box
          sx={{
  mt: isColorCoverFamily
    ? { xs: 2.5, sm: 3 }
    : isFixFamily
    ? { xs: 5.0, sm: 5.6 }
    : { xs: 3.2, sm: 3.8 },

  display: "flex",
  flexDirection: "column",
  gap: 1.2,

  
}}
>
{surpriseMode && (
            <Typography
              sx={{
                fontSize: 12,
                textAlign: "center",
                color: "#ffffff",
                mx: "auto",
                maxWidth: 420,
              }}
            >
              Surprise mode is on ✨ Full gallery unlocks after the event.
            </Typography>
          )}

          <Button
            fullWidth
            onClick={onUploadClick}
            startIcon={<AddIcon sx={{ fontSize: 20 }} />}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              fontSize: 14,
              borderRadius: 999,
              py: 1.1,
             background:
  styleId === "champagne"
    ? "linear-gradient(90deg, #fef3c7, #fed7e2)"
    : buttonColor,

color:
  styleId === "champagne"
    ? "#111827"
    : buttonFontColor,

boxShadow:
  isColorCoverFamily
    ? "0 12px 30px rgba(0,0,0,0.34)"
    : styleId === "champagne"
    ? "0 10px 26px rgba(0,0,0,0.65)"
    : "0 18px 40px rgba(0,0,0,0.75), 0 0 0 1px rgba(248,250,252,0.08)",

"&:hover": {
  background:
    styleId === "champagne"
      ? "linear-gradient(90deg, #fff7ed, #ffe4e6)"
      : buttonColor,
  opacity: 0.98,
},
            }}
          >
            Upload photos
          </Button>
<Button
  fullWidth
  onClick={onTakePhotoClick}
  startIcon={
    styleId === "champagne"
      ? null
      : <PhotoCameraIcon sx={{ fontSize: 20 }} />
  }
  sx={{
    textTransform: "none",

    fontWeight: 600,
fontSize: 14,
borderRadius: 999,
py: 1,

   backgroundColor:
  styleId === "champagne"
    ? "#090d16"
    : secondaryBg,

border:
  styleId === "champagne"
    ? "1.5px solid rgba(254,202,202,0.9)"
    : `1.5px solid ${buttonColor}`,

color:
  styleId === "champagne"
    ? "#fef2f2"
    : secondaryTextColor,

boxShadow:
  styleId === "champagne"
    ? "none"
    : "0 10px 30px rgba(0,0,0,0.4)",

"&:hover": {
  backgroundColor:
    styleId === "champagne"
      ? "#090d16"
      : secondaryBg,
  opacity: 0.97,
},
  }}
>
  Open camera
</Button>
          {!!onViewGalleryClick && isColorCoverFamily && (
            <Button
              fullWidth
              onClick={onViewGalleryClick}
              sx={{
                mt: 1.2,
                px: 0,
                py: 1.5,
                borderRadius: 0,
                color: "#ffffff",
                textTransform: "none",
                borderTop: "1px solid rgba(255,255,255,0.16)",
                borderBottom: "1px solid rgba(255,255,255,0.16)",
                "&:hover": {
                  backgroundColor: "rgba(255,255,255,0.04)",
                },
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    width: 46,
                    height: 46,
                    borderRadius: 999,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "rgba(255,255,255,0.11)",
                    border: "1px solid rgba(255,255,255,0.08)",
                  }}
                >
                  <ImageOutlinedIcon sx={{ fontSize: 24 }} />
                </Box>

                <Typography sx={{ fontSize: 16, fontWeight: 500 }}>
                  View gallery
                </Typography>

                <ChevronRightIcon
                  sx={{ ml: "auto", fontSize: 28, opacity: 0.95 }}
                />
              </Box>
            </Button>
          )}

          {!!onViewGalleryClick && !isColorCoverFamily && (
            <Button
              fullWidth
              onClick={onViewGalleryClick}
              variant="text"
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: 13,
                borderRadius: 999,
                py: 0.8,
                color: "rgba(255,255,255,0.9)",
                textDecoration: "underline",
                "&:hover": {
                  textDecoration: "underline",
                  opacity: 0.9,
                },
              }}
            >
              View gallery
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default EventAccessUI;
